import { useMemo } from 'react'

import {
  BillingPriceInterval,
  BillingProduct,
} from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { PlanCardButton } from './PlanCardButton.js'

interface UpdatePlanCardProps {
  interval: BillingPriceInterval
  product: BillingProduct
}

const UpgradePlanCardContent = ({ product, interval }: UpdatePlanCardProps) => {
  const price = product.prices.find(
    price => price.default && price.interval === interval,
  )
  const amount = useMemo(
    () =>
      price.interval === BillingPriceInterval.Month
        ? price.amount / 100
        : price.amount / 100 / 12,
    [price],
  )

  return (
    <div className="flex flex-col space-y-5 h-full">
      <div className="space-y-2">
        <div className="flex items-end space-s-2 h-12">
          {product.isEnterprise ? (
            <div className="text-heading-md font-bold text-content">
              <T defaultMessage="Custom" id="Admin.Billing.Plans.Custom" />
            </div>
          ) : (
            <>
              <div className="text-heading-lg font-bold text-content">
                {amount.toLocaleString()}
              </div>
              <div className="text-content-subdued">
                <T defaultMessage="USD /mo" id="Generics.Currency.USDMonth" />
              </div>
            </>
          )}
        </div>

        <div className="text-content-subdued font-medium h-8">
          {!product.isEnterprise &&
            price?.interval === BillingPriceInterval.Month && (
              <T
                defaultMessage="Billed monthly"
                id="Admin.Billing.Plans.BilledMonthly"
              />
            )}
          {!product.isEnterprise &&
            price?.interval === BillingPriceInterval.Year && (
              <T
                defaultMessage="Billed yearly"
                id="Admin.Billing.Plans.BilledYearly"
              />
            )}
          {product.isEnterprise && (
            <T defaultMessage="Let's talk" id="Admin.Billing.Plans.LetsTalk" />
          )}
        </div>
      </div>
      <PlanCardButton
        newProduct={product}
        price={price}
        className="h-12 text-md py-0 leading-8"
      />
      <div>
        <div className="font-bold text-lg text-content mt-2">
          <T
            defaultMessage="What’s included"
            id="Admin.Billing.Plans.WhatsIncluded"
          />
        </div>
      </div>
      <div className="flex-grow flex flex-col space-y-5 text-content">
        {product?.features.map(feature => (
          <div key={feature} className="flex space-s-3">
            <div className="bg-surface-positive rounded-full h-6 w-6">
              <SvgIcon
                className="ms-1 h-6 text-content-on-positive shrink-0"
                name="check"
              />
            </div>
            <div>{feature}</div>
          </div>
        ))}
        <div className="flex space-s-3">
          <div className="bg-surface-positive rounded-full h-6 w-6">
            <SvgIcon
              className="ms-1 h-6 text-content-on-positive shrink-0"
              name="check"
            />
          </div>
          <div>
            <Link
              href="https://bettermode.com/pricing"
              target="_blank"
              rel="noopener noreferrer"
            >
              <T
                defaultMessage="See all features"
                id="Admin.Billing.Plans.SeeFeaturesLink"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export const UpgradePlanCard = ({ product, interval }: UpdatePlanCardProps) => {
  return (
    <Card className="grid:col-span-1 border relative">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-content font-medium text-heading-xs">
          {product?.name}
        </h3>
        <div className="text-content-subdued h-8">
          <p>{product?.description}</p>
        </div>
      </div>

      <Card.Content>
        <UpgradePlanCardContent product={product} interval={interval} />
      </Card.Content>
    </Card>
  )
}
